import { useEffect, useMemo, useState } from 'react';
import { useTheme } from '@mui/material';
import AddressInput from '@/components/ui/AddressInput/AddressInput';
import Box from '@/components/ui/Box/Box';
import Button from '@/components/ui/Button/Button';
import FieldWrapper from '@/components/ui/FieldWrapper/FieldWrapper';
import Paper from '@/components/ui/Paper/Paper';
import TextField from '@/components/ui/TextField/TextField';
import Typography from '@/components/ui/Typography/Typography';
import type { Location } from '@/types/types';
import { formatAddress, type ParsedAddress } from '@/helpers/addressParser';
import type {
    LocationDetailsFormValues,
    LocationDetailsProps,
} from './ILocationDetails';
import { styles } from './styles';

const buildInitial = (loc: Location): LocationDetailsFormValues => {
    const formattedAddress = loc.formatted_address?.trim() || formatAddress(loc);
    return {
        nickname: loc.nickname ?? '',
        formatted_address: formattedAddress,
        street_number: loc.street_number ?? '',
        street_name: loc.street_name ?? '',
        subpremise: loc.subpremise ?? '',
        city: loc.city ?? '',
        state: loc.state ?? '',
        zip: loc.zip ?? '',
        country: loc.country ?? '',
        contact_name: loc.contact_info?.name ?? '',
        contact_email: loc.contact_info?.email ?? '',
        contact_phone: loc.contact_info?.phone_number ?? '',
        notes: loc.notes ?? '',
        place_id: loc.place_id,
    };
};

const LocationDetails = ({ location, onSave }: LocationDetailsProps) => {
    const theme = useTheme();
    const [form, setForm] = useState<LocationDetailsFormValues>(() => buildInitial(location));
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setForm(buildInitial(location));
    }, [location]);

    const baseline = useMemo(
        () => JSON.stringify(buildInitial(location)),
        [location],
    );
    const isDirty = JSON.stringify(form) !== baseline;
    const isValid = Boolean(form.nickname.trim() && form.formatted_address.trim());

    const handleSelectAddress = (place: ParsedAddress) => {
        setForm((prev) => ({
            ...prev,
            formatted_address: place.formatted_address ?? '',
            street_number: place.street_number ?? '',
            street_name: place.street_name ?? '',
            city: place.city ?? '',
            state: place.state ?? '',
            zip: place.zip ?? '',
            country: place.country ?? '',
            place_id: place.place_id ?? prev.place_id,
        }));
    };

    const handleSubmit = async () => {
        if (!isValid || !isDirty) return;
        setSaving(true);
        try {
            await onSave(form);
        } finally {
            setSaving(false);
        }
    };

    const handleReset = () => {
        setForm(buildInitial(location));
    };

    return (
        <Paper sx={styles.paper}>
            <Typography sx={styles.title}>
                {form.nickname.trim() || 'Location Nickname'}
            </Typography>
            <Typography sx={styles.sectionLabel}>Details</Typography>
            <Box sx={styles.fields}>
                <FieldWrapper filled={Boolean(form.nickname)} required>
                    <TextField
                        fullWidth
                        label="Location Name"
                        value={form.nickname}
                        required
                        onChange={(e) =>
                            setForm({ ...form, nickname: e.target.value })
                        }
                    />
                </FieldWrapper>
                <FieldWrapper filled={Boolean(form.formatted_address)} required>
                    <AddressInput
                        fullWidth
                        label="Address"
                        value={form.formatted_address}
                        required
                        onChange={(value) =>
                            setForm({ ...form, formatted_address: value })
                        }
                        onSelect={handleSelectAddress}
                    />
                </FieldWrapper>
                <FieldWrapper filled={Boolean(form.subpremise)}>
                    <TextField
                        fullWidth
                        label="Address 2"
                        value={form.subpremise}
                        onChange={(e) =>
                            setForm({ ...form, subpremise: e.target.value })
                        }
                    />
                </FieldWrapper>
                <FieldWrapper filled={Boolean(form.contact_name)}>
                    <TextField
                        fullWidth
                        label="Contact Name"
                        value={form.contact_name}
                        onChange={(e) =>
                            setForm({ ...form, contact_name: e.target.value })
                        }
                    />
                </FieldWrapper>
                <FieldWrapper filled={Boolean(form.contact_email)}>
                    <TextField
                        fullWidth
                        label="Contact Email"
                        type="email"
                        value={form.contact_email}
                        onChange={(e) =>
                            setForm({ ...form, contact_email: e.target.value })
                        }
                    />
                </FieldWrapper>
                <FieldWrapper filled={Boolean(form.contact_phone)}>
                    <TextField
                        fullWidth
                        label="Contact Phone"
                        value={form.contact_phone}
                        onChange={(e) =>
                            setForm({ ...form, contact_phone: e.target.value })
                        }
                    />
                </FieldWrapper>
                <Box>
                    <FieldWrapper filled={Boolean(form.notes)}>
                        <TextField
                            fullWidth
                            label="Notes"
                            multiline
                            minRows={2}
                            value={form.notes}
                            onChange={(e) =>
                                setForm({ ...form, notes: e.target.value })
                            }
                        />
                    </FieldWrapper>
                    <Typography sx={styles.notesHint}>
                        Notes will be visible to repairers
                    </Typography>
                </Box>
            </Box>
            <Box sx={styles.actions}>
                <Button onClick={handleReset} disabled={!isDirty || saving}>
                    Cancel
                </Button>
                <Button
                    sx={styles.saveBtn(theme)}
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!isDirty || !isValid || saving}
                >
                    {saving ? 'Saving…' : 'Save'}
                </Button>
            </Box>
        </Paper>
    );
};

export default LocationDetails;
